/**
 * Gerador do conjunto de dados sintéticos que alimenta o protótipo.
 *
 * O ponto desta camada não é o caso feliz — é fazer o protótipo mostrar também
 * o trabalho difícil. Um protótipo alimentado só por dados bonitos aprova a
 * apresentação do caso feliz, e o caso ruim aparece pela primeira vez em
 * produção, quando já não há tempo de mudar.
 *
 * Descarta-se este arquivo no Epic 3, quando a API real substituir a origem.
 * Nenhum componente de apresentação precisa mudar, porque o formato é o mesmo.
 */

import {
  aggregateTokensPerSec,
  bandwidthEfficiencyPct,
  costPerMillionTokens,
  effectiveBandwidthTbs,
  kvCapacityTokens,
  maxConcurrencyAtTarget,
  requestsPerSec,
  tokensPerWatt,
} from "./capacity.js";
import { SeededRandom, percentile } from "./random.js";
import {
  CHAT_CONTEXT_TOKENS,
  FLOOR_TOKENS_PER_SEC,
  FLOOR_TPOT_MS,
  GPU_BANDWIDTH_TBS,
  GPU_POWER_W,
  KV_BYTES_PER_TOKEN,
  LATENCY_SIGMA,
  LATENCY_SIGMA_SATURATED,
  MODEL_WEIGHTS_MB,
  MTP_ACCEPT_RATE,
  INSTANCE_COST_USD_HOUR,
  RECURRENT_STATE_MB,
  TARGET_TOKENS_PER_SEC,
  TARGET_TPOT_MS,
  TTFT_CEILING_MS,
} from "./scale.js";
import type {
  AdvisorCandidate,
  BenchRun,
  CacheEffectiveness,
  CalibrationResult,
  EngineMetric,
  FixtureSet,
  GpuMetric,
  RequestSample,
  SampleDrop,
  Scenario,
  ScenarioCapacity,
  ScenarioStats,
  Verdict,
} from "./types.js";

/** Semente padrão. Fixa, para que a avaliação do protótipo seja reprodutível. */
export const DEFAULT_SEED = 20260901;

/** Intervalo entre amostras das séries temporais, em segundos. */
const SAMPLE_INTERVAL_SEC = 5;

/** Instante base das séries temporais, para que os eixos não dependam de hoje. */
const BASE_TIMESTAMP = Date.parse("2026-09-01T14:00:00Z");

/** Perfil de um cenário a ser gerado, com o desfecho pretendido. */
interface ScenarioPlan {
  readonly loadProfile: Scenario["loadProfile"];
  readonly concurrency: number;
  readonly contextLen: number;
  readonly turns: number | null;
  readonly outputCache: Scenario["outputCache"];
  readonly radixCache: boolean;
  readonly hierarchicalCache: boolean;
  /** Desfecho pretendido; governa a mediana de latência gerada. */
  readonly verdict: Verdict;
  /** Fração de requisições que falham, entre 0 e 1. */
  readonly failureRate: number;
  /** Amostras descartadas por saturação da fila de escrita. */
  readonly dropped: number;
  /** Força throttle na telemetria deste cenário. */
  readonly throttled: boolean;
}

/** Uma execução planejada, com seus cenários. */
interface RunPlan {
  readonly instanceType: string;
  readonly gpuModel: string;
  readonly gpuCount: number;
  readonly gpuMemoryGb: number;
  readonly topology: BenchRun["topology"];
  readonly telemetryMode: BenchRun["telemetryMode"];
  readonly comparable: boolean;
  readonly notComparableReason: string | null;
  readonly calibrationPassed: boolean;
  readonly scenarios: readonly ScenarioPlan[];
}

/** Quantil 0,95 da distribuição normal padrão. */
const Z_95 = 1.6449;

/**
 * Razão entre percentil 95 e mediana numa log-normal com a dispersão dada.
 *
 * Numa log-normal, mediana = exp(mu) e p95 = exp(mu + z·sigma), então a razão
 * entre os dois depende apenas de sigma.
 */
function p95OverMedian(sigma: number): number {
  return Math.exp(Z_95 * sigma);
}

/**
 * Mediana de latência por token que produz o desfecho pretendido.
 *
 * ⚠️ A mediana é **derivada do percentil 95 desejado**, e não escolhida
 * diretamente. O motivo é que a classificação avalia o percentil 95, e numa
 * distribuição de cauda à direita ele fica bem acima da mediana — calibrar pela
 * mediana produz cenário classificado uma faixa abaixo do pretendido.
 *
 * Os alvos de percentil ficam deliberadamente afastados das fronteiras: um
 * valor exatamente no limite tornaria o desfecho sensível ao ruído da geração,
 * e o protótipo mostraria classificação diferente a cada ajuste de semente.
 */
function medianTpotFor(verdict: Verdict, sigma: number): number {
  const ratio = p95OverMedian(sigma);
  switch (verdict) {
    case "meets":
      // Percentil 95 a 80% do alvo: atende com folga.
      return (TARGET_TPOT_MS * 0.8) / ratio;
    case "acceptable":
      // Percentil 95 no meio da faixa entre o alvo e o piso.
      return ((TARGET_TPOT_MS + FLOOR_TPOT_MS) / 2) / ratio;
    case "fails":
      // Percentil 95 a 50% acima do piso: reprova sem depender de arredondamento.
      return (FLOOR_TPOT_MS * 1.5) / ratio;
  }
}

/** Plano completo do conjunto. Descreve o que cada execução existe para mostrar. */
const RUN_PLANS: readonly RunPlan[] = [
  // 1) O candidato de compra sob carga conversacional: o caso que ATENDE.
  {
    instanceType: "g6e.12xlarge",
    gpuModel: "L40S",
    gpuCount: 4,
    gpuMemoryGb: 48,
    topology: "disaggregated",
    telemetryMode: "measure",
    comparable: true,
    notComparableReason: null,
    calibrationPassed: true,
    scenarios: [
      {
        loadProfile: "chat",
        concurrency: 8,
        contextLen: CHAT_CONTEXT_TOKENS,
        turns: 10,
        outputCache: "exact",
        radixCache: true,
        hierarchicalCache: true,
        verdict: "meets",
        failureRate: 0,
        dropped: 0,
        throttled: false,
      },
      {
        loadProfile: "chat",
        concurrency: 32,
        contextLen: CHAT_CONTEXT_TOKENS,
        turns: 10,
        outputCache: "exact",
        radixCache: true,
        hierarchicalCache: true,
        verdict: "meets",
        failureRate: 0,
        dropped: 0,
        throttled: false,
      },
      {
        loadProfile: "chat",
        concurrency: 64,
        contextLen: CHAT_CONTEXT_TOKENS,
        turns: 10,
        outputCache: "semantic",
        radixCache: true,
        hierarchicalCache: true,
        verdict: "meets",
        failureRate: 0,
        dropped: 0,
        throttled: false,
      },
      // Mesma carga sem cache de prefixo: isola o ganho do cache.
      {
        loadProfile: "chat",
        concurrency: 64,
        contextLen: CHAT_CONTEXT_TOKENS,
        turns: 10,
        outputCache: "off",
        radixCache: false,
        hierarchicalCache: false,
        verdict: "acceptable",
        failureRate: 0,
        dropped: 0,
        throttled: false,
      },
    ],
  },
  // 2) O degrau de validação, menor: fica na faixa ACEITÁVEL.
  {
    instanceType: "g6e.2xlarge",
    gpuModel: "L40S",
    gpuCount: 1,
    gpuMemoryGb: 48,
    topology: "aggregated",
    telemetryMode: "measure",
    comparable: true,
    notComparableReason: null,
    calibrationPassed: true,
    scenarios: [
      {
        loadProfile: "chat",
        concurrency: 8,
        contextLen: CHAT_CONTEXT_TOKENS,
        turns: 10,
        outputCache: "off",
        radixCache: true,
        hierarchicalCache: false,
        verdict: "acceptable",
        failureRate: 0,
        dropped: 0,
        throttled: false,
      },
      // Caso adverso: falha parcial de requisições e descarte contabilizado.
      {
        loadProfile: "chat",
        concurrency: 64,
        contextLen: CHAT_CONTEXT_TOKENS,
        turns: 10,
        outputCache: "off",
        radixCache: true,
        hierarchicalCache: false,
        verdict: "fails",
        failureRate: 0.07,
        dropped: 1_483,
        throttled: false,
      },
    ],
  },
  // 3) Contexto longo no mesmo candidato: o caso que REPROVA por memória.
  {
    instanceType: "g6e.12xlarge",
    gpuModel: "L40S",
    gpuCount: 4,
    gpuMemoryGb: 48,
    topology: "disaggregated",
    telemetryMode: "profile",
    comparable: true,
    notComparableReason: null,
    calibrationPassed: true,
    scenarios: [
      {
        loadProfile: "document",
        concurrency: 64,
        contextLen: 131_072,
        turns: null,
        outputCache: "off",
        radixCache: true,
        hierarchicalCache: true,
        verdict: "fails",
        failureRate: 0.02,
        dropped: 0,
        throttled: false,
      },
    ],
  },
  // 4) Execução NÃO COMPARÁVEL: throttle térmico detectado durante a medição.
  {
    instanceType: "g6e.48xlarge",
    gpuModel: "L40S",
    gpuCount: 8,
    gpuMemoryGb: 48,
    topology: "disaggregated",
    telemetryMode: "measure",
    comparable: false,
    notComparableReason:
      "Throttle térmico detectado em 3 dos 8 aceleradores durante 41% da execução; " +
      "os relógios variaram e os resultados não são comparáveis com as demais execuções.",
    calibrationPassed: true,
    scenarios: [
      {
        loadProfile: "chat",
        concurrency: 64,
        contextLen: CHAT_CONTEXT_TOKENS,
        turns: 10,
        outputCache: "off",
        radixCache: true,
        hierarchicalCache: true,
        verdict: "acceptable",
        failureRate: 0,
        dropped: 0,
        throttled: true,
      },
    ],
  },
];

/** Formata um instante deslocado do início, em segundos, no padrão ISO. */
function timestampAt(offsetSeconds: number): string {
  return new Date(BASE_TIMESTAMP + offsetSeconds * 1_000).toISOString();
}

/**
 * Taxa de acerto de prefixo esperada num dado turno.
 *
 * Cresce ao longo da conversa: o turno 1 não encontra nada em cache, e a partir
 * do turno 2 cada requisição reaproveita todo o histórico anterior. É esse
 * comportamento que torna o dimensionamento do projeto viável, e a curva
 * subindo é a prova visual de que o cache está trabalhando.
 */
export function prefixHitRateForTurn(turnIndex: number, radixCache: boolean): number {
  if (!radixCache) {
    return 0;
  }
  if (turnIndex <= 1) {
    return 0;
  }
  // Aproxima a fração do prompt que já foi vista, saturando na faixa de 70-90%.
  const growth = 1 - 1 / turnIndex;
  return Math.min(0.9, growth * 0.95);
}

/** Memória ocupada pelo cache de estado, em megabytes, para o cenário. */
export function kvMemoryMb(contextLen: number, concurrency: number): number {
  const perSessionMb = (KV_BYTES_PER_TOKEN * contextLen) / (1024 * 1024);
  return (perSessionMb + RECURRENT_STATE_MB) * concurrency;
}

/** Gera as amostras de requisição de um cenário. */
function buildSamples(
  rng: SeededRandom,
  scenario: Scenario,
  plan: ScenarioPlan,
): RequestSample[] {
  const samples: RequestSample[] = [];
  // Cenário saturado tem cauda mais pesada — é isso que separa mediana de p95.
  const sigma = plan.verdict === "fails" ? LATENCY_SIGMA_SATURATED : LATENCY_SIGMA;
  const medianTpot = medianTpotFor(plan.verdict, sigma);
  const turns = plan.turns ?? 1;
  // Amostragem parcial das sessões: o suficiente para a distribuição, sem
  // inflar o conjunto a ponto de pesar no carregamento do protótipo.
  const sessions = Math.min(plan.concurrency, 24);

  for (let session = 0; session < sessions; session += 1) {
    const sessionId = `s-${scenario.id}-${String(session).padStart(3, "0")}`;
    for (let turn = 1; turn <= turns; turn += 1) {
      const failed = rng.bool(plan.failureRate);
      const hitRate = prefixHitRateForTurn(turn, plan.radixCache);

      // Em chat o contexto cresce turno a turno; em requisição isolada é fixo.
      const tokensIn =
        plan.turns === null
          ? plan.contextLen
          : Math.round((plan.contextLen * turn) / turns);

      // O prefixo já em cache não é reprocessado, o que reduz o tempo até o
      // primeiro token proporcionalmente.
      const ttftBase = plan.turns === null ? 2_400 : 420;
      const ttftMs = rng.logNormal(ttftBase * (1 - hitRate * 0.7), sigma);

      const tpotMs = rng.logNormal(medianTpot, sigma);
      const tokensOut = rng.int(180, 620);

      // Cache de saída responde sem tocar no acelerador: latência de milissegundos.
      const fromCache =
        !failed && plan.outputCache !== "off" && rng.bool(plan.outputCache === "semantic" ? 0.18 : 0.06);

      samples.push({
        scenarioId: scenario.id,
        traceId: `t-${scenario.id}-${session}-${turn}`,
        sessionId,
        turnIndex: turn,
        ttftMs: fromCache ? rng.uniform(2, 6) : ttftMs,
        tpotMs: fromCache ? rng.uniform(0.05, 0.2) : tpotMs,
        itlP50Ms: fromCache ? null : tpotMs * rng.uniform(0.9, 1.1),
        tokensIn,
        tokensOut: failed ? 0 : tokensOut,
        servedFrom: fromCache
          ? plan.outputCache === "semantic"
            ? "cache_semantic"
            : "cache_exact"
          : "engine",
        ok: !failed,
        error: failed ? "connection reset by peer durante a transmissão" : null,
      });
    }
  }
  return samples;
}

/** Gera a telemetria de acelerador de um cenário, coerente com seu desfecho. */
function buildGpuMetrics(
  rng: SeededRandom,
  scenario: Scenario,
  plan: ScenarioPlan,
  run: RunPlan,
): GpuMetric[] {
  const metrics: GpuMetric[] = [];
  const nominalPower = GPU_POWER_W[run.gpuModel] ?? 350;
  const memTotalMb = run.gpuMemoryGb * 1024;
  const kvMb = kvMemoryMb(plan.contextLen, plan.concurrency) / run.gpuCount;
  const weightsMb = MODEL_WEIGHTS_MB / run.gpuCount;

  // Coerência interna: um cenário que reprova por saturação não pode aparecer
  // com o acelerador ocioso — isso desviaria a avaliação para discutir o dado
  // em vez da tela.
  const baseUtil = plan.verdict === "fails" ? 96 : plan.verdict === "acceptable" ? 88 : 74;
  const samplesPerScenario = 40;

  for (let step = 0; step < samplesPerScenario; step += 1) {
    for (let gpu = 0; gpu < run.gpuCount; gpu += 1) {
      // O throttle atinge parte dos aceleradores, e só na segunda metade da
      // execução — é assim que ele aparece de verdade, por aquecimento.
      const throttled = plan.throttled && gpu < 3 && step > samplesPerScenario / 2;
      const utilPct = Math.min(100, rng.normal(baseUtil, 3));

      metrics.push({
        scenarioId: scenario.id,
        ts: timestampAt(step * 5),
        gpuIdx: gpu,
        utilPct,
        memUsedMb: Math.min(memTotalMb * 0.97, weightsMb + kvMb + rng.uniform(200, 900)),
        // Potência acompanha a utilização; sob throttle ela cai junto do relógio.
        powerW: throttled
          ? nominalPower * rng.uniform(0.62, 0.72)
          : nominalPower * (utilPct / 100) * rng.uniform(0.9, 1.0),
        tempC: throttled ? rng.uniform(84, 89) : rng.uniform(62, 74),
        smClockMhz: throttled ? rng.int(1_100, 1_300) : rng.int(2_100, 2_300),
        memClockMhz: rng.int(8_800, 9_000),
        throttleReason: throttled ? "SW_THERMAL_SLOWDOWN" : null,
        // Campos de perfilamento só existem no modo de diagnóstico.
        dramActive: run.telemetryMode === "profile" ? rng.uniform(0.68, 0.83) : null,
        smOccupancy: run.telemetryMode === "profile" ? rng.uniform(0.41, 0.58) : null,
        nvlinkBytes: null,
      });
    }
  }
  return metrics;
}

/**
 * Gera as métricas internas do motor para um cenário.
 *
 * ⚠️ O contador de tokens gerados precisa ser **coerente com a vazão do
 * cenário**, e não um número arbitrário: a tela ao vivo deriva a latência por
 * token a partir dele, e um contador fora de escala produz um gráfico cujo eixo
 * não comporta a linha do alvo. Defeito encontrado ao desenhar a tela — é
 * exatamente o tipo de incoerência que o protótipo existe para revelar.
 */
function buildEngineMetrics(
  rng: SeededRandom,
  scenario: Scenario,
  plan: ScenarioPlan,
  medianTpotMs: number,
): EngineMetric[] {
  const metrics: EngineMetric[] = [];
  const turns = plan.turns ?? 1;
  const samplesPerScenario = 40;
  let tokensGenerated = 0;

  for (let step = 0; step < samplesPerScenario; step += 1) {
    // A fila cresce quando o cenário reprova — é a explicação da latência alta.
    const queueBase = plan.verdict === "fails" ? plan.concurrency * 0.45 : 1.5;
    // Percorre os turnos ao longo do tempo, para que o acerto de prefixo suba.
    const turnAtStep = 1 + Math.floor((step / samplesPerScenario) * turns);
    const runningBatch = Math.min(
      plan.concurrency,
      Math.max(1, Math.round(rng.normal(plan.concurrency * 0.8, 4))),
    );
    // Tokens no intervalo = requisições ativas × vazão por requisição × duração.
    // A amostragem é a cada 5 s, e a vazão é o inverso da latência por token.
    const tokensPerRequestPerSec = 1_000 / medianTpotMs;
    const generatedInStep = runningBatch * tokensPerRequestPerSec * SAMPLE_INTERVAL_SEC;
    tokensGenerated += Math.round(generatedInStep * rng.uniform(0.92, 1.08));

    metrics.push({
      scenarioId: scenario.id,
      ts: timestampAt(step * 5),
      queueSize: Math.max(0, Math.round(rng.normal(queueBase, 1.5))),
      runningBatch,
      prefixHitRate: prefixHitRateForTurn(turnAtStep, plan.radixCache),
      tokensGenerated,
      mtpAcceptRate: rng.normal(MTP_ACCEPT_RATE, 0.02),
      kvCacheUsedPct: Math.min(99, rng.normal(plan.verdict === "fails" ? 94 : 61, 3)),
    });
  }
  return metrics;
}

/** Calcula as estatísticas agregadas de um cenário a partir de suas amostras. */
function buildStats(scenarioId: number, samples: readonly RequestSample[]): ScenarioStats {
  const ok = samples.filter((s) => s.ok);
  const ttfts = ok.map((s) => s.ttftMs);
  const tpots = ok.map((s) => s.tpotMs);

  const tpotP95 = percentile(tpots, 95) ?? 0;
  const tokensPerSecP95 = tpotP95 > 0 ? 1_000 / tpotP95 : 0;
  const ttftP95 = percentile(ttfts, 95) ?? 0;

  // Goodput é a fração de requisições que sustentam o alvo — calculado sobre as
  // amostras individuais, nunca sobre a média.
  const meeting = ok.filter((s) => 1_000 / s.tpotMs >= TARGET_TOKENS_PER_SEC).length;
  const goodputPct = ok.length > 0 ? (meeting / ok.length) * 100 : 0;

  let verdict: Verdict;
  if (tokensPerSecP95 >= TARGET_TOKENS_PER_SEC && ttftP95 < TTFT_CEILING_MS) {
    verdict = "meets";
  } else if (tokensPerSecP95 >= FLOOR_TOKENS_PER_SEC && ttftP95 <= TTFT_CEILING_MS * 2) {
    verdict = "acceptable";
  } else {
    verdict = "fails";
  }

  return {
    scenarioId,
    sampleCount: samples.length,
    okCount: ok.length,
    ttftP50Ms: percentile(ttfts, 50) ?? 0,
    ttftP95Ms: ttftP95,
    ttftP99Ms: percentile(ttfts, 99) ?? 0,
    tpotP50Ms: percentile(tpots, 50) ?? 0,
    tpotP95Ms: tpotP95,
    tpotP99Ms: percentile(tpots, 99) ?? 0,
    tokensPerSecP95,
    goodputPct,
    verdict,
  };
}

/**
 * Monta as métricas de capacidade e eficiência de um cenário.
 *
 * São as que respondem "quantos usuários cabem e a que custo" — a pergunta de
 * dimensionamento, distinta da de latência.
 */
function buildCapacity(
  scenario: Scenario,
  plan: ScenarioPlan,
  run: RunPlan,
  stats: ScenarioStats,
  samples: readonly RequestSample[],
  gpuMetrics: readonly GpuMetric[],
): ScenarioCapacity {
  const aggregate = aggregateTokensPerSec(plan.concurrency, stats.tpotP95Ms);
  const okSamples = samples.filter((s) => s.ok && s.tokensOut > 0);
  const avgTokensOut =
    okSamples.length > 0
      ? okSamples.reduce((sum, s) => sum + s.tokensOut, 0) / okSamples.length
      : 0;

  // Potência somada dos aceleradores no instante de pico observado.
  const byStep = new Map<string, number>();
  for (const metric of gpuMetrics) {
    byStep.set(metric.ts, (byStep.get(metric.ts) ?? 0) + metric.powerW);
  }
  const powerTotal = Math.max(...[...byStep.values(), 0]);

  const effective = effectiveBandwidthTbs(
    plan.contextLen,
    plan.concurrency,
    stats.tpotP95Ms,
    scenario.mtpTokens,
  );
  const efficiency = bandwidthEfficiencyPct(effective, run.gpuModel, run.gpuCount);
  const capacityTokens = kvCapacityTokens(run.gpuMemoryGb * run.gpuCount, plan.concurrency);
  const usedTokens = plan.contextLen * plan.concurrency;
  const costHour = INSTANCE_COST_USD_HOUR[run.instanceType] ?? 0;

  const itls = okSamples
    .map((s) => s.itlP50Ms)
    .filter((v): v is number => v !== null);

  return {
    scenarioId: scenario.id,
    aggregateTokensPerSec: aggregate,
    requestsPerSec: requestsPerSec(aggregate, avgTokensOut),
    maxConcurrencyAtTarget: maxConcurrencyAtTarget(
      plan.contextLen,
      run.gpuModel,
      run.gpuCount,
      efficiency,
      scenario.mtpTokens,
    ),
    kvCapacityTokens: capacityTokens,
    kvUsedPct: capacityTokens > 0 ? Math.min(100, (usedTokens / capacityTokens) * 100) : 100,
    tokensPerWatt: tokensPerWatt(aggregate, powerTotal),
    costPerMillionTokensUsd: costPerMillionTokens(costHour, aggregate),
    itlP95Ms: percentile(itls, 95) ?? 0,
    effectiveBandwidthTbs: effective,
    bandwidthEfficiencyPct: efficiency,
  };
}

/** Monta o inventário de hardware detectado, com consumo. */
function buildHardware(rng: SeededRandom, run: RunPlan, gpuMetrics: readonly GpuMetric[]) {
  const bandwidthEach = GPU_BANDWIDTH_TBS[run.gpuModel] ?? 0.864;
  const powerTotal = gpuMetrics
    .filter((m) => m.ts === gpuMetrics[0]?.ts)
    .reduce((sum, m) => sum + m.powerW, 0);
  // Energia por integração da potência ao longo do tempo da execução.
  const durationHours = (40 * 5) / 3_600;
  const avgPower =
    gpuMetrics.reduce((sum, m) => sum + m.powerW, 0) / Math.max(1, gpuMetrics.length);

  return {
    instanceType: run.instanceType,
    availabilityZone: "us-east-1a",
    gpuModel: run.gpuModel,
    gpuCount: run.gpuCount,
    gpuMemoryGbEach: run.gpuMemoryGb,
    gpuBandwidthTbsEach: bandwidthEach,
    // L40S não tem interconexão dedicada — é uma das limitações da emulação.
    nvlink: false,
    cpuModel: "Intel Xeon Platinum 8488C",
    cpuCores: run.gpuCount * 12,
    ramGb: run.gpuCount * 96,
    powerWTotal: powerTotal,
    energyKwhRun: (avgPower * run.gpuCount * durationHours) / 1_000,
    throttling: gpuMetrics.some((m) => m.throttleReason !== null),
    bandwidthEfficiencyPct: rng.uniform(64, 78),
  };
}

/** Vida útil assumida do hardware, em horas — quatro anos de operação contínua. */
const HARDWARE_LIFETIME_HOURS = 4 * 365 * 24;

/** Fator de refrigeração assumido sobre o consumo dos aceleradores. */
const COOLING_FACTOR = 1.4;

/** Tarifa industrial de energia, em dólares por quilowatt-hora. */
const ENERGY_USD_KWH = 0.75 / 5.4;

/**
 * Métricas de escala de um candidato, derivadas do desempenho previsto.
 *
 * Todas herdam a mesma ressalva do desempenho de que derivam: se aquele é
 * extrapolado, estas também são, e a limitação viaja junto. Calcular sem
 * propagar a ressalva produziria números que parecem medidos.
 */
function scaleMetricsFor(
  perRequestTps: number,
  sessions: number,
  powerW: number,
  capexUsd: number,
  limitation: string,
): Pick<
  AdvisorCandidate,
  | "predictedAggregateTokensPerSec"
  | "costPerMillionTokensUsd"
  | "tokensPerWatt"
  | "tokensPerSecPerKUsd"
  | "concurrentSessionsAtTarget"
> {
  const aggregate = perRequestTps * sessions;
  // Custo por hora de posse: compra amortizada mais energia com refrigeração.
  const capexPerHour = capexUsd / HARDWARE_LIFETIME_HOURS;
  const energyPerHour = (powerW / 1_000) * COOLING_FACTOR * ENERGY_USD_KWH;
  const tokensPerHour = aggregate * 3_600;
  const costPerMillion =
    tokensPerHour > 0 ? ((capexPerHour + energyPerHour) / tokensPerHour) * 1_000_000 : 0;

  return {
    predictedAggregateTokensPerSec: {
      value: aggregate,
      provenance: "extrapolated",
      limitation,
    },
    costPerMillionTokensUsd: {
      value: costPerMillion,
      provenance: "extrapolated",
      limitation:
        "Inclui compra amortizada em 4 anos e energia com fator de refrigeração 1,4. " +
        "Não inclui espaço físico, rede nem operação.",
    },
    tokensPerWatt: {
      value: powerW > 0 ? aggregate / powerW : 0,
      provenance: "extrapolated",
      limitation,
    },
    tokensPerSecPerKUsd: {
      value: capexUsd > 0 ? aggregate / (capexUsd / 1_000) : 0,
      provenance: "extrapolated",
      limitation,
    },
    concurrentSessionsAtTarget: {
      value: sessions,
      provenance: "extrapolated",
      limitation,
    },
  };
}

/**
 * Candidatos de compra apresentados pelo Advisor.
 *
 * Inclui deliberadamente um candidato **não emulável**, que é o caso mais
 * difícil de comunicar bem e, por isso, o que mais se beneficia de acordo
 * prévio com o time.
 */
function buildAdvisor(): AdvisorCandidate[] {
  const notEmulableLimitation =
    "Não estimável a partir das medições disponíveis: nenhum degrau da bancada possui " +
    "interconexão dedicada, e o ganho de paralelismo entre aceleradores não é transferível.";
  const emulationLimitation =
    "Extrapolado de 4× L40S: a bancada tem mais aceleradores com menos banda cada e " +
    "sem interconexão dedicada. O resultado é piso, não estimativa — o candidato deve ir melhor.";

  return [
    {
      id: "rtx6000pro-x2",
      label: "2× RTX 6000 Pro Blackwell 96 GB",
      gpuModel: "RTX 6000 Pro",
      gpuCount: 2,
      totalVramGb: 192,
      totalBandwidthTbs: 3.6,
      predictedTokensPerSec: {
        value: 118,
        provenance: "extrapolated",
        limitation: emulationLimitation,
      },
      vramHeadroomPct: { value: 74, provenance: "measured", limitation: null },
      capexUsd: 19_000,
      opexUsdYear: 2_390,
      ...scaleMetricsFor(118, 64, 1600, 19000, emulationLimitation),
      paybackMonths: {
        value: 9.5,
        provenance: "extrapolated",
        limitation: "Depende do regime de uso assumido: 8 h/dia em 250 dias úteis.",
      },
      powerW: 1_600,
      meetsTarget: true,
      notEmulableReason: null,
    },
    {
      id: "rtx6000pro-x4",
      label: "4× RTX 6000 Pro Blackwell 96 GB",
      gpuModel: "RTX 6000 Pro",
      gpuCount: 4,
      totalVramGb: 384,
      totalBandwidthTbs: 7.2,
      predictedTokensPerSec: {
        value: 214,
        provenance: "extrapolated",
        limitation: emulationLimitation,
      },
      vramHeadroomPct: { value: 87, provenance: "measured", limitation: null },
      capexUsd: 44_000,
      opexUsdYear: 4_770,
      ...scaleMetricsFor(214, 128, 2800, 44000, emulationLimitation),
      paybackMonths: {
        value: 11.2,
        provenance: "extrapolated",
        limitation: "Depende do regime de uso assumido: 8 h/dia em 250 dias úteis.",
      },
      powerW: 2_800,
      meetsTarget: true,
      notEmulableReason: null,
    },
    {
      id: "rtx5090-x2",
      label: "2× RTX 5090 32 GB",
      gpuModel: "RTX 5090",
      gpuCount: 2,
      totalVramGb: 64,
      totalBandwidthTbs: 3.58,
      predictedTokensPerSec: {
        value: 96,
        provenance: "extrapolated",
        limitation: emulationLimitation,
      },
      vramHeadroomPct: {
        value: 12,
        provenance: "extrapolated",
        limitation:
          "Margem apertada: 64 GB acomodam pesos e estado a 8k, mas sem folga para " +
          "fragmentação do cache sob concorrência alta.",
      },
      capexUsd: 14_000,
      opexUsdYear: 1_710,
      ...scaleMetricsFor(96, 32, 1150, 14000, emulationLimitation),
      paybackMonths: {
        value: 8.1,
        provenance: "extrapolated",
        limitation: "Depende do regime de uso assumido: 8 h/dia em 250 dias úteis.",
      },
      powerW: 1_150,
      meetsTarget: false,
      notEmulableReason: null,
    },
    {
      id: "h200-x8",
      label: "8× H200 SXM 141 GB (HGX)",
      gpuModel: "H200",
      gpuCount: 8,
      totalVramGb: 1_128,
      totalBandwidthTbs: 38.4,
      predictedTokensPerSec: {
        value: 0,
        provenance: "extrapolated",
        limitation:
          "Não estimável a partir das medições disponíveis: nenhum degrau da bancada " +
          "possui interconexão dedicada, e o ganho de paralelismo entre aceleradores " +
          "não é transferível.",
      },
      vramHeadroomPct: { value: 0, provenance: "extrapolated", limitation: null },
      capexUsd: 280_000,
      opexUsdYear: 45_800,
      ...scaleMetricsFor(0, 0, 8_400, 280_000, notEmulableLimitation),
      paybackMonths: { value: 0, provenance: "extrapolated", limitation: null },
      powerW: 8_400,
      meetsTarget: false,
      notEmulableReason:
        "Sem instância de bancada equivalente disponível: a única com interconexão " +
        "dedicada exigiria reserva de capacidade antecipada, de custo mínimo superior " +
        "ao orçamento da campanha. Este candidato só pode ser recomendado por " +
        "extrapolação, com incerteza que não permite sustentar decisão de compra.",
    },
  ];
}

/**
 * Gera o conjunto completo de dados sintéticos.
 *
 * @param seed semente da geração; o padrão é fixo para tornar a avaliação
 *             do protótipo reprodutível entre avaliadores.
 */
export function generateFixtures(seed: number = DEFAULT_SEED): FixtureSet {
  const rng = new SeededRandom(seed);

  const runs: BenchRun[] = [];
  const scenarios: Scenario[] = [];
  const samples: RequestSample[] = [];
  const gpuMetrics: GpuMetric[] = [];
  const engineMetrics: EngineMetric[] = [];
  const calibrations: CalibrationResult[] = [];
  const drops: SampleDrop[] = [];
  const cacheEffectiveness: CacheEffectiveness[] = [];
  const stats: ScenarioStats[] = [];
  const capacity: ScenarioCapacity[] = [];

  let scenarioId = 1;

  RUN_PLANS.forEach((plan, runIndex) => {
    const runId = runIndex + 1;

    runs.push({
      id: runId,
      startedAt: timestampAt(runIndex * 3_600),
      finishedAt: timestampAt(runIndex * 3_600 + 1_800),
      instanceType: plan.instanceType,
      gpuModel: plan.gpuModel,
      gpuCount: plan.gpuCount,
      gpuMemoryGb: plan.gpuMemoryGb,
      engine: plan.topology === "disaggregated" ? "sglang+dynamo" : "sglang",
      engineVersion: "0.4.9",
      model: "Qwen/Qwen3.8-27B-FP8",
      quantization: "fp8",
      topology: plan.topology,
      telemetryMode: plan.telemetryMode,
      launchCommand:
        `python -m sglang.launch_server --model-path Qwen/Qwen3.8-27B-FP8 ` +
        `--tp-size ${plan.gpuCount} --kv-cache-dtype fp8_e4m3 ` +
        `--speculative-algorithm EAGLE --speculative-num-draft-tokens 3 ` +
        `--mem-fraction-static 0.85 --chunked-prefill-size 8192 --port 30000`,
      lockedClockMhz: 2_200,
      comparable: plan.comparable,
      notComparableReason: plan.notComparableReason,
    });

    // A calibração precisa existir e estar aprovada para o resultado ser oficial.
    const measureDelta = rng.uniform(0.2, 0.8);
    calibrations.push({
      runId,
      throughputOff: 100,
      throughputMeasure: 100 - measureDelta,
      throughputProfile: 100 - rng.uniform(3.5, 6.0),
      measureDeltaPct: measureDelta,
      profileDeltaPct: rng.uniform(3.5, 6.0),
      passed: plan.calibrationPassed,
    });

    plan.scenarios.forEach((sc) => {
      const scenario: Scenario = {
        id: scenarioId,
        runId,
        loadProfile: sc.loadProfile,
        concurrency: sc.concurrency,
        contextLen: sc.contextLen,
        turns: sc.turns,
        mtpTokens: 3,
        prefixHitMode: "natural",
        prefixHitPct: null,
        outputCache: sc.outputCache,
        radixCache: sc.radixCache,
        hierarchicalCache: sc.hierarchicalCache,
        kvDtype: "fp8_e4m3",
        status: "done",
        failureReason: null,
      };
      scenarios.push(scenario);

      const scenarioSamples = buildSamples(rng, scenario, sc);
      samples.push(...scenarioSamples);
      const scenarioStats = buildStats(scenario.id, scenarioSamples);
      stats.push(scenarioStats);
      const scenarioGpu = buildGpuMetrics(rng, scenario, sc, plan);
      gpuMetrics.push(...scenarioGpu);
      capacity.push(
        buildCapacity(scenario, sc, plan, scenarioStats, scenarioSamples, scenarioGpu),
      );
      const sigma = sc.verdict === "fails" ? LATENCY_SIGMA_SATURATED : LATENCY_SIGMA;
      engineMetrics.push(
        ...buildEngineMetrics(rng, scenario, sc, medianTpotFor(sc.verdict, sigma)),
      );

      if (sc.dropped > 0) {
        drops.push({
          scenarioId: scenario.id,
          dropped: sc.dropped,
          reason: "fila de escrita saturada durante o pico de concorrência",
        });
      }

      if (sc.outputCache === "exact") {
        cacheEffectiveness.push({
          scenarioId: scenario.id,
          level: "exact",
          hitRatePct: rng.uniform(6, 11),
          // Cache exato não erra por construção.
          falsePositivePct: null,
          similarityThreshold: null,
        });
      }
      if (sc.outputCache === "semantic") {
        cacheEffectiveness.push({
          scenarioId: scenario.id,
          level: "exact",
          hitRatePct: rng.uniform(6, 11),
          falsePositivePct: null,
          similarityThreshold: null,
        });
        // Caso adverso obrigatório: acerto ALTO com falso positivo ALTO.
        // Acerto sozinho é métrica de vaidade — a tela precisa mostrar os dois.
        cacheEffectiveness.push({
          scenarioId: scenario.id,
          level: "semantic",
          hitRatePct: rng.uniform(58, 67),
          falsePositivePct: rng.uniform(9, 14),
          similarityThreshold: 0.91,
        });
      }

      scenarioId += 1;
    });
  });

  // O hardware apresentado é o da execução do candidato principal.
  const primaryRunMetrics = gpuMetrics.filter((m) => m.scenarioId <= 4);
  const primaryPlan = RUN_PLANS[0];
  if (primaryPlan === undefined) {
    throw new Error("generateFixtures: plano de execução principal ausente");
  }

  return {
    runs,
    scenarios,
    samples,
    gpuMetrics,
    engineMetrics,
    calibrations,
    drops,
    cacheEffectiveness,
    stats,
    capacity,
    hardware: buildHardware(rng, primaryPlan, primaryRunMetrics),
    advisor: buildAdvisor(),
  };
}
